import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { api } from "../api/client.js";
import { PageHeader } from "../components/AppLayout.js";
import { Tabs, type TabDef } from "../components/ui/tabs.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { formatRange } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import { StatusBadge, eventStatusVariant } from "./shared/pills.js";
import { AttendeesTab } from "./event-detail/AttendeesTab.js";
import { IntegrationTab } from "./event-detail/IntegrationTab.js";
import { OperatorsTab } from "./event-detail/OperatorsTab.js";
import { OverviewTab } from "./event-detail/OverviewTab.js";
import { ScanLogTab } from "./event-detail/ScanLogTab.js";
import { StagesTab } from "./event-detail/StagesTab.js";

const TABS: TabDef[] = [
  { id: "overview", label: "Overview" },
  { id: "stages", label: "Stages" },
  { id: "operators", label: "Operators" },
  { id: "attendees", label: "Attendees" },
  { id: "integration", label: "Integration" },
  { id: "scans", label: "Scan log" }
];

/** Event detail with five tabs (build plan P2-T8). */
export function EventDetailPage(): JSX.Element {
  const { eventId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") ?? "overview";
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.id === tabParam) ? tabParam : "overview"
  );

  const { data: event, loading, error, reload } = useAsync(
    () => api.getEvent(eventId),
    [eventId]
  );

  const onTabChange = (id: string): void => {
    setActiveTab(id);
    searchParams.set("tab", id);
    setSearchParams(searchParams, { replace: true });
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Event" />
        <LoadingState />
      </>
    );
  }

  if (error || !event) {
    return (
      <>
        <PageHeader title="Event" />
        <ErrorState message={error ?? "Failed to load."} onRetry={reload} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={event.name}
        description={
          <span className="flex items-center gap-3">
            <Link to="/events" className="text-accent hover:underline">
              ← All events
            </Link>
            <span className="text-muted-foreground">
              {formatRange(event.active_from, event.active_to)}
            </span>
          </span>
        }
        actions={
          <StatusBadge variant={eventStatusVariant(event.status)}>
            {event.status}
          </StatusBadge>
        }
      />

      <Tabs tabs={TABS} active={activeTab} onChange={onTabChange} />

      <div className="pt-6">
        {activeTab === "overview" ? (
          <OverviewTab event={event} onUpdated={reload} />
        ) : null}
        {activeTab === "stages" ? <StagesTab event={event} /> : null}
        {activeTab === "operators" ? <OperatorsTab event={event} /> : null}
        {activeTab === "attendees" ? <AttendeesTab event={event} /> : null}
        {activeTab === "integration" ? <IntegrationTab event={event} /> : null}
        {activeTab === "scans" ? <ScanLogTab event={event} /> : null}
      </div>
    </>
  );
}
